import React, { useState } from "react";
import { ModalX, PageHeader } from "../../components";
import { Button, Checkbox, IconButton, Input, Option, Select, Spinner, Textarea } from "@material-tailwind/react";
import { Edit2Icon } from "lucide-react";
import { NotifyError } from "../../utils/functions";
import { useAddBuilding, useGetBuildings, useUpdateBuilding } from "../../custom-hooks/use-buildings";
import { useGetBranches } from "../../custom-hooks/use-branches";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { usePermissions } from "../../custom-hooks/use-permissions";


const INITIAL_FORM_DATA = {
  _id: '',
  branchId: '',
  name: '',
  location: '',
  totalFloors: 0,
  floorsOccupied: 0,
  buildingCode: '',
  address: '',
  constructionYear: '',
  architect: '',
  description: '',
  photos: [],
  status: '',
  openingHours: null,
  safetyCertificate: null,
  fireSafetyStatus: false,
  legalClearance: false,
  emergencyExits: 0,
  totalCapacity: 0,
  internetAvailability: false,
  powerBackup: false,
  facilities: [],
  washrooms: 0,
  elevators: 0,
  parkingCapacity: 0
}

const BuildingPage = () => {

  // State variables
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
  // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);
  // TODO: Un comment when adding file upload
  // const [safetyCertificateFile, setSafetyCertificateFile] = useState(null);
  // const [safetyCertificateFileUrl, setSafetyCertificateFileUrl] = useState('');
  // const safetyCertificateFileInputRef = useRef(null);

  const { data: buildings, isLoading: isBuildingsLoading, isError: isBuildingFetchError } = useGetBuildings();
  const { data: branches, isLoading: isBranchesLoading, isError: isBranchesFetchError } = useGetBranches();

  const addBuildingMutation = useAddBuilding();
  const updateBuildingMutation = useUpdateBuilding();

  const { can } = usePermissions();


  // Handle form input change
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = async (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUpdating) {
        const updatedBuilding = {
          name: formData.name,
          location: formData.location,
          totalFloors: formData.totalFloors,
          floorsOccupied: formData.floorsOccupied,
          buildingCode: formData.buildingCode,
          address: formData.address,
          constructionYear: formData.constructionYear,
          architect: formData.architect,
          description: formData.description,
          photos: formData.photos,
          status: formData.status,
          openingHours: formData.openingHours,
          safetyCertificate: formData.safetyCertificate,
          fireSafetyStatus: formData.fireSafetyStatus,
          legalClearance: formData.legalClearance,
          emergencyExits: formData.emergencyExits,
          totalCapacity: formData.totalCapacity,
          internetAvailability: formData.internetAvailability,
          powerBackup: formData.powerBackup,
          facilities: formData.facilities,
          washrooms: formData.washrooms,
          elevators: formData.elevators,
          parkingCapacity: formData.parkingCapacity
        };
        await updateBuildingMutation.mutateAsync({ buildingId: selectedBuildingId, data: updatedBuilding });
      } else {
        // Add new building logic
        const newBuilding = {
          name: formData.name,
          location: formData.location,
          totalFloors: formData.totalFloors,
          floorsOccupied: formData.floorsOccupied,
          buildingCode: formData.buildingCode,
          address: formData.address,
          constructionYear: formData.constructionYear,
          architect: formData.architect,
          description: formData.description,
          photos: formData.photos,
          status: formData.status,
          openingHours: formData.openingHours,
          safetyCertificate: formData.safetyCertificate,
          fireSafetyStatus: formData.fireSafetyStatus,
          legalClearance: formData.legalClearance,
          emergencyExits: formData.emergencyExits,
          totalCapacity: formData.totalCapacity,
          internetAvailability: formData.internetAvailability,
          powerBackup: formData.powerBackup,
          facilities: formData.facilities,
          washrooms: formData.washrooms,
          elevators: formData.elevators,
          parkingCapacity: formData.parkingCapacity
        };
        await addBuildingMutation.mutateAsync({ branchId: formData.branchId, data: newBuilding });
      }
    } catch (error) {
      NotifyError(error.response.data.message, error);
    } finally {
      setModalOpen(false);
      setFormData(INITIAL_FORM_DATA);
      setIsUpdating(false);
      setSelectedBuildingId(null);
    }
  };

  // Open modal for adding a new building
  const openAddBuildingModal = () => {
    setModalOpen(true);
    const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
    defaultValueForInitialFormData.status = 'operational';

    setFormData(defaultValueForInitialFormData);
    setIsUpdating(false);
  };

  // Open modal for updating a building
  const openUpdateBuildingModal = (building) => {
    setModalOpen(true);
    setFormData({ ...building, branchId: building.branch._id });
    setIsUpdating(true);
    setSelectedBuildingId(building._id);
  };

  // TODO: Un comment when adding file upload
  // const handleFileChange = (event) => {
  //   const file = event.target.files[0];
  //   setSafetyCertificateFile(file);
  //   // You can add your file handling logic here
  //   console.log('Selected file:', file);
  //   // Clear previous preview
  //   if (safetyCertificateFileUrl) {
  //     URL.revokeObjectURL(safetyCertificateFileUrl);
  //   }

  //   // Create preview URL for images
  //   if (file && file.type.startsWith('image/')) {
  //     const url = URL.createObjectURL(file);
  //     setSafetyCertificateFileUrl(url);
  //   } else {
  //     setSafetyCertificateFileUrl('');
  //   }
  // };

  // TODO: Un comment when adding file upload
  // const isPDF = safetyCertificateFile?.type === 'application/pdf';
  // const isImage = safetyCertificateFile?.type.startsWith('image/');

  return (
    <div className={``}>

      <div className="m-5 p-5 border rounded-3xl">
        <PageHeader
          title="Building Management"
          isLoading={isBuildingsLoading}
          actionOnClick={can('FX5', 'edit') ? openAddBuildingModal : undefined}
          actionType="add"
          actionText="Add Building"
        />
        <div className="overflow-x-auto">
          {isBuildingsLoading ? (
            <div className="flex justify-center py-5">
              <Spinner />
            </div>
          ) : isBuildingFetchError ? (
            <div className="text-center py-5 text-red-500">
              Error loading buildings. Please try again.
            </div>
          ) : (
            <table className="w-full min-w-max table-auto text-left">
              <thead className=" text-sm">
                <tr className="py-3">
                  <th colSpan={4} className="py-3 text-xs font-light text-orange-900">List of All Buildings ({buildings?.length || 0})</th>
                  {/* <th colSpan={1} className="py-3">
                  <Input
                    label="Search"
                  />
                </th> */}
                </tr>
                <tr className="py-3 uppercase">
                  <th className="py-3">Name</th>
                  <th className="py-3">Code</th>
                  <th className="py-3">Branch</th>
                  <th className="py-3">Status</th>
                  <th className="py-3">Floors</th>
                  {can('FX5', 'edit') && (
                  <th className="py-3">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {buildings?.map((building, index) => (
                  <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                    <td className="font-thin py-3 text-sm">
                      {building.name}
                    </td>
                    <td className="font-thin py-3 text-sm">
                      {building.buildingCode}
                    </td>
                    <td className="font-thin py-3 text-sm">
                      {building.branch.name}
                    </td>

                    <td className="font-thin py-3 text-sm">
                      <div className="flex">
                        {InterpretOperationalStatus(building.status)}
                      </div>
                    </td>
                    <td className="font-thin py-3 text-sm">
                      <div className="text-xs">
                        Occupied: <span className={building.floorsOccupied > 0 ? "font-bold" : ""}>{building.floorsOccupied}</span>
                      </div>
                      <div className="text-xs">
                        Total: <span className={building.totalFloors > 0 ? "font-bold" : ""}>{building.totalFloors}</span>
                      </div>
                    </td>
                    {can('FX5', 'edit') && (
                    <td className="flex gap-1 py-3">
                      {/* <IconButton
                      size="sm"
                      className="rounded-full"
                      onClick={() => openUpdateBuildingModal(building)}
                    >
                      <EyeIcon size={12} />
                    </IconButton> */}
                      <IconButton
                        size="sm"
                        color="orange"
                        className="rounded-full"
                        onClick={() => openUpdateBuildingModal(building)}
                      >
                        <Edit2Icon size={12} />
                      </IconButton>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>

            </table>)}
        </div>

        {/* Modal for Add/Update Building */}
        <ModalX isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isUpdating ? "Update Building" : "Add Building"}
          maxWidth="xxl">
          <form onSubmit={handleFormSubmit}>
            <div className="space-y-2">
              {
                isBranchesFetchError && (
                  <div className="text-center py-5 text-red-500">
                    Error loading branches. Please check your internet connection and try again.
                  </div>
                )
              }
              <div className="">
                <Select
                  label={<>Branch <span className="text-red-500 ml-1">*</span></>}
                  value={formData.branchId}
                  disabled={isBranchesLoading}
                  onChange={(newValue) => handleSelectChange("branchId", newValue)}
                >
                  {
                    branches?.map((branch, index) => (
                      <Option key={index} value={branch._id}>
                        <div className="flex justify-between gap-3 w-full">
                          <div className="">
                            {branch.branchName}
                          </div>
                          <div className="">
                            {branch.city}, {branch.country}
                          </div>
                        </div>
                      </Option>
                    ))
                  }
                </Select>
              </div>
              <div className="flex flex-col md:flex-row gap-2">
                <Input
                  label="Building Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  label="Building Code"
                  name="buildingCode"
                  value={formData.buildingCode}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="">
                <Input
                  label="Complete Address"
                  name="address"
                  required
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex gap-2 flex-col md:flex-row">

                <Input
                  label="Architect Name - Phone"
                  name="architect"
                  value={formData.architect}
                  onChange={handleInputChange}
                />
                <Select
                  label="Construction Year"
                  value={formData.constructionYear.toString()}
                  onChange={newValue => handleSelectChange("constructionYear", newValue)}
                >
                  {
                    Array.from({ length: 76 }, (_, i) => i + 1950).map(year => (
                      <Option key={year} value={year.toString()}>{year}</Option>
                    ))
                  }
                </Select>
                <Select
                  label="Status"
                  value={formData.status}
                  onChange={newValue => handleSelectChange("status", newValue)}
                >
                  <Option value={'operational'}>Operational</Option>
                  <Option value={'in_maintenance'}>In Maintenance</Option>
                  <Option value={'under_construction'}>Under Construction</Option>
                  <Option value={'closed'}>Closed</Option>
                </Select>
              </div>

              <div className="flex gap-2 flex-col md:flex-row">
                <div className="mt-5 w-full ">
                  <Input
                    label={`Total Floors ${formData.totalFloors}`}
                    name="totalFloors"
                    type="range"
                    className="text-black"
                    value={formData.totalFloors}
                    onChange={handleInputChange}
                  />
                </div>
                {/* <Input
                  label={`Occupied Floors ${formData.floorsOccupied}`}
                  name="floorsOccupied"
                  type="range"
                  value={formData.floorsOccupied}
                  onChange={handleInputChange}
                /> */}
                {/* <Input
                  label={`Total Seat Capacity ${formData.totalCapacity}`}
                  name="totalCapacity"
                  type="text"
                  value={formData.totalCapacity}
                  onChange={handleInputChange}
                /> */}
              </div>

              {/* <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div> */}
              {/* <div className="flex gap-4">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                />

                <Input
                  label="Longitude"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
                <Input
                  label="Latitude"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  type="number"
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
              </div> */}

              <div className="flex gap-3 flex-col md:flex-row">
                {/* <div className="flex flex-col items-center">
                  <div className="text-sm font-thin">Safety Certificate</div>
                  <Button
                    onClick={() => safetyCertificateFileInputRef.current.click()}
                    className={`mt-2 p-0 border border-dashed h-56 w-36 flex flex-col justify-center items-center`}
                    // style={{ backgroundImage: `url(${safetyCertificateFile})` }}
                    variant="text">
                    <ImageIcon className="text-gray-400" />
                    {!safetyCertificateFile && (
                      <div className="flex items-center gap-2 text-gray-400 font-thin">
                        Choose File
                      </div>
                    )}

                    {isPDF && (
                      <div className="flex flex-col items-center gap-2 text-gray-700">
                        <FileTextIcon size={24} />
                        <span className="text-sm font-medium break-all max-w-full px-2">
                          {safetyCertificateFile.name}
                        </span>
                      </div>
                    )}

                    {isImage && safetyCertificateFileUrl && (
                      <>
                        <img
                          src={safetyCertificateFileUrl}
                          alt="Preview"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-30 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white font-thin gap-2">
                          <ImageIcon size={20} className="mr-2" />
                          Change File
                        </div>
                      </>
                    )}
                  </Button>
                  <input
                    type="file"
                    ref={safetyCertificateFileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  // You can add accept="image/*" or other attributes to limit file types
                  />
                </div> */}
                <div className="border-l pl-3 w-full">
                  <Checkbox
                    name="fireSafetyStatus"
                    checked={formData.fireSafetyStatus}
                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                    label="Fire Safety"
                    onChange={handleCheckBoxChange}
                  />
                  <Checkbox
                    name="legalClearance"
                    checked={formData.legalClearance}
                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                    label="Legal Clearence"
                    onChange={handleCheckBoxChange}
                  />
                  <Checkbox
                    name="internetAvailability"
                    checked={formData.internetAvailability}
                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                    label="Internet"
                    onChange={handleCheckBoxChange}
                  />
                  <Checkbox
                    name="powerBackup"
                    checked={formData.powerBackup}
                    className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                    label="Power Backup"
                    onChange={handleCheckBoxChange}
                  />
                  <hr className="mb-2" />
                  <div className="flex gap-2 flex-col md:flex-row">
                    <Input
                      label="Emergency Exits"
                      type="number"
                      name="emergencyExits"
                      value={formData.emergencyExits}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Washrooms"
                      type="number"
                      name="washrooms"
                      value={formData.washrooms}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="flex gap-2 mt-2 flex-col md:flex-row">
                    <Input name="elevators" value={formData.elevators} label="Elevators" type="number" onChange={handleInputChange} />
                    <Input name="parkingCapacity" value={formData.parkingCapacity} label="Parking Capacity" type="number" onChange={handleInputChange} />
                  </div>
                  <div className="text-start mt-2">
                    {/* <Select  label="Facilities/Ameneties" value={formData.facilities} onChange={handleSelectChange}>
                      <Option value="parking">Parking</Option>
                      <Option value="gym">Gym</Option>
                      <Option value="cafeteria">Cafeteria</Option>
                      <Option value="prayer-room">Prayer Room</Option>
                      <Option value="reception">Reception</Option>
                      <Option value="game-room">Game Room</Option>
                      <Option value="public-area">Public area</Option>
                      <Option value="smoking-area">Smoking area</Option>
                      <Option value="private-area">Private area</Option>
                      <Option value="guard">Guard</Option>
                      <Option value="storage">Storage</Option>
                      <Option value="server-room">Server room</Option>
                      <Option value="power-room">Power room</Option>
                      <Option value="kitchen">Kitchen</Option>
                      <Input label="Add New" icon={<IconButton variant="outlined" className="h-4 w-4"><PlusIcon size={16}/></IconButton>} />
                    </Select> */}
                    <Textarea
                      label="Additional Comments"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={2}
                    />
                  </div>

                  {/* <div className="text-sm font-thin">Photos</div> */}
                  {/* <Button className="mt-2 p-0 border border-dashed h-20 w-20 flex justify-center items-center" variant="text">
                    <PlusIcon className="text-gray-400" />
                  </Button> */}
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <Button
                onClick={() => setModalOpen(false)}
                className="rounded-full "
                variant="text"
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-full">
                {isUpdating ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </ModalX>
      </div>

    </div>
  );
};

export default BuildingPage;
